import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { Lead, PipelineStage, Activity } from '@/lib/types';
import {
  formatDate, formatDateTime, haceCuanto, SOURCE_LABELS, ZONE_LABELS, LANGUAGE_LABELS, PROPERTY_STATE_LABELS, getInitials,
} from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import Modal from '@/components/ui/Modal';
import ActivityTimeline from '@/components/owners/ActivityTimeline';
import BloqueTareas from '@/components/tareas/BloqueTareas';
import {
  Search, Phone, Mail, UserCheck, Trash2, Facebook, Plus, Loader2,
  MapPin, Home, ChevronRight, Filter, X, GitBranch, FileText, Clock, MessageCircle,
} from 'lucide-react';
import BotonLlamar from '@/components/centralita/BotonLlamar';
import { useAutores } from '@/lib/autores';
import WhatsAppLead from '@/components/leads/WhatsAppLead';

export default function LeadsPage() {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [converting, setConverting] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<Lead | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState<Lead | null>(null);
  const [saving, setSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Stage-change activity modal
  const [stageChangeModal, setStageChangeModal] = useState<{ lead: Lead; newStageId: string } | null>(null);
  const [stageActivityType, setStageActivityType] = useState('call');
  const [stageActivityTitle, setStageActivityTitle] = useState('');
  const [stageActivityNote, setStageActivityNote] = useState('');
  const [stageChangeSaving, setStageChangeSaving] = useState(false);
  const [newLead, setNewLead] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    source: 'puerta_fria', preferredLanguage: 'es', notes: '',
    propertyAddress: '', propertyZone: '', propertyType: 'apartamento',
    propertyBedrooms: '', propertyCurrentState: '',
  });

  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (sourceFilter) params.source = sourceFilter;
  if (stageFilter) params.pipelineStageId = stageFilter;

  const { data: leads, loading, refresh } = useApi<Lead[]>(
    () => api.getLeads(Object.keys(params).length > 0 ? params : undefined),
    [search, sourceFilter, stageFilter]
  );

  const { data: stages } = useApi<PipelineStage[]>(() => api.getStages(), []);

  /**
   * `?lead=<id>` abre ese lead directamente.
   *
   * Los leads no tienen página propia —su detalle es un modal de esta lista—,
   * así que sin esto no había forma de enlazar a uno. Lo usan las tareas
   * relacionadas con un lead, que sí necesitan llevar a alguna parte.
   *
   * Se lee del servidor y no de la lista cargada porque los filtros activos
   * pueden dejarlo fuera, y entonces el enlace no llevaría a ningún sitio.
   */
  useEffect(() => {
    const id = urlParams.get('lead');
    if (!id || showDetail?.id === id) return;

    let vigente = true;
    api.getLead(id)
      .then(l => { if (vigente) setShowDetail(l); })
      .catch(() => { /* un id que ya no existe no debe romper la lista */ });
    return () => { vigente = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams]);

  /** Tras cambios hechos fuera del formulario (p. ej. «Procesar conversación»): la ficha y la lista, al día. */
  const recargarDetalle = (id: string) => {
    api.getLead(id).then(l => setShowDetail(prev => (prev?.id === id ? l : prev))).catch(() => undefined);
    refresh();
  };

  /** Cierra el detalle y se lleva por delante el `?lead=` que lo abrió. */
  const cerrarDetalle = () => {
    setShowDetail(null);
    if (urlParams.get('lead')) {
      const siguientes = new URLSearchParams(urlParams);
      siguientes.delete('lead');
      setUrlParams(siguientes, { replace: true });
    }
  };

  const stageMap = new Map(stages?.map(s => [s.id, s]) || []);

  const stageCounts = new Map<string, number>();
  leads?.forEach(l => {
    const sid = l.pipelineStageId || 'unknown';
    stageCounts.set(sid, (stageCounts.get(sid) || 0) + 1);
  });

  const handleConvert = async (lead: Lead) => {
    if (!confirm(`¿Convertir a ${lead.firstName} ${lead.lastName} en propietario?`)) return;
    setConverting(lead.id);
    try {
      const result = await api.convertLead(lead.id);
      if (result.ownerId) navigate(`/owners/${result.ownerId}`);
    } catch (err) {
      console.error('Convert error:', err);
      alert('Error al convertir el lead');
    } finally {
      setConverting(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.deleteLead(deleteModal.id);
      setDeleteModal(null);
      setShowDetail(null);
      refresh();
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error al eliminar el lead');
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { propertyBedrooms, ...rest } = newLead;
      await api.createLead({
        ...rest,
        propertyBedrooms: propertyBedrooms ? Number(propertyBedrooms) : undefined,
      });
      setShowCreate(false);
      setNewLead({
        firstName: '', lastName: '', email: '', phone: '',
        source: 'puerta_fria', preferredLanguage: 'es', notes: '',
        propertyAddress: '', propertyZone: '', propertyType: 'apartamento',
        propertyBedrooms: '', propertyCurrentState: '',
      });
      refresh();
    } catch (err) {
      console.error('Create error:', err);
      alert('Error al crear el lead');
    } finally {
      setSaving(false);
    }
  };

  const handleStageChange = (lead: Lead, newStageId: string) => {
    if (newStageId === lead.pipelineStageId) return;
    setStageActivityType('call');
    setStageActivityTitle('');
    setStageActivityNote('');
    setStageChangeModal({ lead, newStageId });
  };

  const guardarActividadDelCambio = async (lead: Lead) => {
    if (!stageActivityTitle.trim()) return;
    // Lo mismo que «Nueva actividad» del detalle: `entityId` es la clave de
    // partición de `activities` y lo que lee la línea de tiempo del lead.
    // Con `leadId` la actividad no salía en ninguna ficha.
    await api.createActivity({
      entityId: lead.id,
      entityType: 'lead',
      activityType: stageActivityType,
      title: stageActivityTitle.trim(),
      description: stageActivityNote.trim() || undefined,
    });
  };

  const executeStageChange = async (withActivity: boolean) => {
    if (!stageChangeModal) return;
    const { lead, newStageId } = stageChangeModal;
    setStageChangeSaving(true);
    try {
      const guardado = await api.updateLead(lead.id, { pipelineStageId: newStageId });
      if (withActivity) await guardarActividadDelCambio(lead);
      // Con lo que devuelve el servidor y no solo la fase: así el detalle abierto
      // estrena «en esta fase desde» y «última modificación», y vuelve a pedir
      // la actividad, que ya trae el cambio.
      setShowDetail(prev => prev?.id === lead.id ? { ...prev, pipelineStageId: newStageId, ...guardado } : prev);
      refresh();
      setStageChangeModal(null);
    } catch (err) {
      console.error('Stage update error:', err);
    } finally {
      setStageChangeSaving(false);
    }
  };

  // Mover a «Ganado» solo cambia la fase: el propietario lo crea la conversión,
  // que además deja el lead en Ganado. Por eso, desde el cambio de fase, se
  // convierte en vez de mover.
  const convertirDesdeCambioDeFase = async () => {
    if (!stageChangeModal) return;
    const { lead } = stageChangeModal;
    setStageChangeSaving(true);
    try {
      await guardarActividadDelCambio(lead);
      const result = await api.convertLead(lead.id);
      setStageChangeModal(null);
      if (result.ownerId) navigate(`/owners/${result.ownerId}`);
    } catch (err) {
      console.error('Convert error:', err);
      alert('Error al convertir el lead');
    } finally {
      setStageChangeSaving(false);
    }
  };

  const handleSaveDetail = async (lead: Lead, updates: Record<string, any>) => {
    setSaving(true);
    try {
      await api.updateLead(lead.id, updates);
      setShowDetail(null);
      refresh();
    } catch (err) {
      console.error('Update error:', err);
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const activeFilters = [sourceFilter, stageFilter].filter(Boolean).length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Pipeline de Leads</h1>
          <p className="text-sm text-sereno-dark/40 mt-0.5">{leads?.length ?? 0} lead{(leads?.length ?? 0) !== 1 ? 's' : ''} en seguimiento</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-sm flex items-center gap-1.5">
          <Plus size={16} /> Nuevo lead
        </button>
      </div>

      {/* Pipeline Stage Summary Bar */}
      {stages && stages.length > 0 && (
        <div className="mb-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-1">
            <button
              onClick={() => setStageFilter('')}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${!stageFilter ? 'bg-sereno-dark text-white shadow-sm' : 'bg-white text-sereno-dark/60 hover:bg-gray-50 border border-gray-200'}`}
            >
              Todos ({leads?.length || 0})
            </button>
            {stages.sort((a, b) => a.order - b.order).map(stage => {
              const count = stageCounts.get(stage.id) || 0;
              const isActive = stageFilter === stage.id;
              return (
                <button
                  key={stage.id}
                  onClick={() => setStageFilter(isActive ? '' : stage.id)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap flex items-center gap-2 ${isActive ? 'text-white shadow-sm' : 'bg-white text-sereno-dark/60 hover:bg-gray-50 border border-gray-200'}`}
                  style={isActive ? { backgroundColor: stage.color } : {}}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                  {stage.name}
                  {count > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/25' : 'bg-gray-100'}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input
              type="text"
              placeholder="Buscar por nombre, email, teléfono..."
              className="input pl-9"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${activeFilters > 0 ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-sereno-dark/60 hover:bg-gray-50'}`}
          >
            <Filter size={14} />
            Filtros
            {activeFilters > 0 && (
              <span className="bg-blue-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{activeFilters}</span>
            )}
          </button>
        </div>
        {showFilters && (
          <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-100">
            <select className="input w-auto text-sm" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
              <option value="">Todas las fuentes</option>
              <option value="facebook">Facebook Ads</option>
              {Object.entries(SOURCE_LABELS).filter(([k]) => k !== 'facebook').map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            {(sourceFilter || stageFilter) && (
              <button
                onClick={() => { setSourceFilter(''); setStageFilter(''); }}
                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              >
                <X size={12} /> Limpiar filtros
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lead List */}
      {loading ? (
        <LoadingSpinner />
      ) : !leads || leads.length === 0 ? (
        <EmptyState
          title="No hay leads"
          description={stageFilter || sourceFilter ? 'Prueba a cambiar los filtros' : 'Los leads de Facebook y otras fuentes aparecerán aquí'}
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full hidden lg:table">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-5 py-3">Lead</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-5 py-3">Contacto</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-5 py-3">Propiedad</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-5 py-3">Fuente</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-5 py-3">Fase</th>
                <th className="text-right text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-5 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => {
                const stage = stageMap.get(lead.pipelineStageId);
                const isClosed = stage?.isClosed;
                const isLost = stage?.isLost;
                return (
                  <tr key={lead.id} className="border-b border-gray-50 hover:bg-sereno-blue/10 transition-colors cursor-pointer" onClick={() => setShowDetail(lead)}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${isClosed ? 'bg-green-100 text-green-700' : isLost ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white'}`}>
                          {getInitials(lead.firstName, lead.lastName)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{lead.firstName} {lead.lastName}</p>
                          <p className="text-[11px] text-sereno-dark/35">{formatDate(lead.createdAt)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-0.5 text-sm text-sereno-dark/60">
                        {lead.phone && (
                          <a href={`tel:${lead.phone}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 hover:text-sereno-dark">
                            <Phone size={12} /> {lead.phone}
                          </a>
                        )}
                        {lead.email && (
                          <a href={`mailto:${lead.email}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 hover:text-sereno-dark truncate max-w-[200px]">
                            <Mail size={12} /> {lead.email}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {lead.propertyAddress || lead.propertyZone ? (
                        <div className="text-sm text-sereno-dark/60">
                          {lead.propertyAddress && <p className="flex items-center gap-1 truncate max-w-[180px]"><MapPin size={12} className="flex-shrink-0" /> {lead.propertyAddress}</p>}
                          {lead.propertyZone && <p className="text-[11px] text-sereno-dark/35">{ZONE_LABELS[lead.propertyZone] || lead.propertyZone}</p>}
                        </div>
                      ) : (
                        <span className="text-xs text-sereno-dark/25">Sin datos</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        {lead.source === 'facebook' && <Facebook size={12} className="text-blue-500" />}
                        <span className="text-xs text-sereno-dark/50">{SOURCE_LABELS[lead.source] || lead.source}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3" onClick={e => e.stopPropagation()}>
                      <select
                        className="text-xs rounded-lg px-2.5 py-1.5 border-0 font-medium text-white cursor-pointer shadow-sm"
                        style={{ backgroundColor: stage?.color || '#6B7280' }}
                        value={lead.pipelineStageId || ''}
                        onChange={e => handleStageChange(lead, e.target.value)}
                      >
                        {stages?.sort((a, b) => a.order - b.order).map(s => (
                          <option key={s.id} value={s.id} style={{ backgroundColor: 'white', color: '#0B1D2E' }}>{s.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {/* Llamada directa desde el CRM, igual que en el historial
                            de llamadas. El componente se apaga solo si la
                            centralita no está lista o el número no vale. */}
                        <BotonLlamar
                          numero={lead.phone}
                          nombre={`${lead.firstName} ${lead.lastName}`.trim()}
                          alojamiento={lead.propertyZone || lead.propertyAddress || null}
                          compacto
                        />
                        {/* Por la marca de convertido y no por la fase: un lead
                            movido a «Ganado» a mano sigue sin propietario. */}
                        {!isLost && !lead.convertedToOwnerId && (
                          <button
                            onClick={() => handleConvert(lead)}
                            disabled={converting === lead.id}
                            className="btn-primary text-xs py-1 px-2.5 flex items-center gap-1"
                          >
                            {converting === lead.id ? <Loader2 size={12} className="animate-spin" /> : <UserCheck size={12} />}
                            <span className="hidden xl:inline">Convertir</span>
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteModal(lead)}
                          className="p-1.5 rounded hover:bg-red-50 text-sereno-dark/25 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-gray-50">
            {leads.map(lead => {
              const stage = stageMap.get(lead.pipelineStageId);
              const isClosed = stage?.isClosed;
              const isLost = stage?.isLost;
              return (
                <div key={lead.id} className="p-4 hover:bg-sereno-blue/5 transition-colors" onClick={() => setShowDetail(lead)}>
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${isClosed ? 'bg-green-100 text-green-700' : isLost ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white'}`}>
                      {getInitials(lead.firstName, lead.lastName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-sm truncate">{lead.firstName} {lead.lastName}</p>
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full text-white flex-shrink-0" style={{ backgroundColor: stage?.color || '#6B7280' }}>
                          {stage?.name || 'Sin fase'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-sereno-dark/40">
                        {lead.source === 'facebook' && <Facebook size={11} className="text-blue-500" />}
                        <span>{SOURCE_LABELS[lead.source] || lead.source}</span>
                        <span>·</span>
                        <span>{formatDate(lead.createdAt)}</span>
                      </div>
                      {lead.propertyAddress && (
                        <p className="text-xs text-sereno-dark/40 mt-1 flex items-center gap-1 truncate">
                          <MapPin size={11} className="flex-shrink-0" /> {lead.propertyAddress}
                        </p>
                      )}
                      {lead.phone && (
                        <p className="text-xs text-sereno-dark/50 mt-1 flex items-center gap-1">
                          <Phone size={11} /> {lead.phone}
                        </p>
                      )}
                    </div>
                    {/* En pantalla estrecha la tabla no se pinta, así que el botón
                        de llamar tiene que estar también aquí o no existe en móvil. */}
                    <div onClick={e => e.stopPropagation()} className="flex-shrink-0">
                      <BotonLlamar
                        numero={lead.phone}
                        nombre={`${lead.firstName} ${lead.lastName}`.trim()}
                        alojamiento={lead.propertyZone || lead.propertyAddress || null}
                        compacto
                      />
                    </div>
                    <ChevronRight size={16} className="text-sereno-dark/20 mt-3 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      <Modal isOpen={!!showDetail} onClose={cerrarDetalle} title="Detalle del lead" size="2xl">
        {showDetail && <LeadDetailPanel lead={showDetail} stages={stages || []} stageMap={stageMap} onStageChange={handleStageChange} onConvert={handleConvert} onDelete={l => setDeleteModal(l)} onSave={handleSaveDetail} saving={saving} converting={converting} onRecargar={recargarDetalle} />}
      </Modal>

      {/* Create Lead Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Nuevo lead">
        <form onSubmit={handleCreate} className="space-y-4">
          <p className="text-xs text-sereno-dark/40 -mt-2 mb-3">Información del contacto</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Nombre *</label>
              <input className="input" required value={newLead.firstName} onChange={e => setNewLead(f => ({ ...f, firstName: e.target.value }))} />
            </div>
            <div>
              <label className="label">Apellidos *</label>
              <input className="input" required value={newLead.lastName} onChange={e => setNewLead(f => ({ ...f, lastName: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Teléfono</label>
              <input className="input" value={newLead.phone} onChange={e => setNewLead(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" value={newLead.email} onChange={e => setNewLead(f => ({ ...f, email: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Fuente *</label>
              <select className="input" required value={newLead.source} onChange={e => setNewLead(f => ({ ...f, source: e.target.value }))}>
                {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Idioma</label>
              <select className="input" value={newLead.preferredLanguage} onChange={e => setNewLead(f => ({ ...f, preferredLanguage: e.target.value }))}>
                {Object.entries(LANGUAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs text-sereno-dark/40 mb-3 flex items-center gap-1"><Home size={12} /> Información de la propiedad</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="label">Dirección de la propiedad</label>
                <input className="input" placeholder="Calle, número, piso..." value={newLead.propertyAddress} onChange={e => setNewLead(f => ({ ...f, propertyAddress: e.target.value }))} />
              </div>
              <div>
                <label className="label">Zona</label>
                <select className="input" value={newLead.propertyZone} onChange={e => setNewLead(f => ({ ...f, propertyZone: e.target.value }))}>
                  <option value="">Sin especificar</option>
                  {Object.entries(ZONE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Tipo de propiedad</label>
                <select className="input" value={newLead.propertyType} onChange={e => setNewLead(f => ({ ...f, propertyType: e.target.value }))}>
                  {['apartamento', 'casa', 'chalet', 'atico', 'estudio', 'villa', 'adosado'].map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Habitaciones</label>
                <input type="number" className="input" value={newLead.propertyBedrooms} onChange={e => setNewLead(f => ({ ...f, propertyBedrooms: e.target.value }))} />
              </div>
              <div>
                <label className="label">Estado actual</label>
                <select className="input" value={newLead.propertyCurrentState} onChange={e => setNewLead(f => ({ ...f, propertyCurrentState: e.target.value }))}>
                  <option value="">Sin especificar</option>
                  <option value="empty">Vacía</option>
                  <option value="owner_use">Uso del propietario</option>
                  <option value="long_term_rental">Alquiler largo plazo</option>
                  <option value="already_tourist_rental">Ya alquiler turístico</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="label">Notas</label>
            <textarea className="input" rows={3} value={newLead.notes} onChange={e => setNewLead(f => ({ ...f, notes: e.target.value }))} placeholder="Cualquier información relevante..." />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-ghost" onClick={() => setShowCreate(false)}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-1.5" disabled={saving}>
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              {saving ? 'Creando...' : 'Crear lead'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal isOpen={!!deleteModal} onClose={() => setDeleteModal(null)} title="Eliminar lead">
        {deleteModal && (
          <div className="space-y-4">
            <p className="text-sm">
              ¿Seguro que quieres eliminar el lead <strong>{deleteModal.firstName} {deleteModal.lastName}</strong>?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button className="btn-ghost" onClick={() => setDeleteModal(null)}>Cancelar</button>
              <button
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm flex items-center gap-1.5"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Stage change activity modal */}
      <Modal isOpen={!!stageChangeModal} onClose={() => !stageChangeSaving && setStageChangeModal(null)} title="Cambio de fase">
        {stageChangeModal && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-sereno-dark/60 bg-gray-50 rounded-lg px-3 py-2">
              <GitBranch size={14} className="flex-shrink-0" />
              <span>
                Moviendo a <strong className="text-sereno-dark">{stageMap.get(stageChangeModal.newStageId)?.name}</strong>
              </span>
            </div>
            {stageMap.get(stageChangeModal.newStageId)?.isClosed
              && !stageMap.get(stageChangeModal.newStageId)?.isLost
              && !stageChangeModal.lead.convertedToOwnerId && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-3 space-y-2">
                <p className="text-sm text-sereno-dark/70">
                  Cambiar la fase no crea el propietario. ¿Convertir a <strong className="text-sereno-dark">{stageChangeModal.lead.firstName} {stageChangeModal.lead.lastName}</strong> en propietario? Se crea su ficha, sus contratos pasan a ella y el lead queda en Ganado.
                </p>
                <button
                  className="btn-primary text-sm flex items-center gap-1.5"
                  onClick={convertirDesdeCambioDeFase}
                  disabled={stageChangeSaving}
                >
                  {stageChangeSaving ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} />}
                  Convertir en propietario
                </button>
              </div>
            )}
            <p className="text-sm text-sereno-dark/60">¿Quieres registrar una actividad que justifique este cambio?</p>
            <div>
              <label className="label">Tipo de actividad</label>
              <select className="input" value={stageActivityType} onChange={e => setStageActivityType(e.target.value)}>
                <option value="call">Llamada</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
                <option value="visit">Visita</option>
                <option value="meeting">Reunión</option>
                <option value="note">Nota</option>
              </select>
            </div>
            <div>
              <label className="label">Título / resumen</label>
              <input
                className="input"
                placeholder="Ej: Llamada de seguimiento, visita realizada..."
                value={stageActivityTitle}
                onChange={e => setStageActivityTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Notas (opcional)</label>
              <textarea
                className="input resize-none"
                rows={3}
                placeholder="Detalles adicionales..."
                value={stageActivityNote}
                onChange={e => setStageActivityNote(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <button
                className="btn-ghost"
                onClick={() => executeStageChange(false)}
                disabled={stageChangeSaving}
              >
                Omitir
              </button>
              <button
                className="btn-primary flex items-center gap-1.5"
                onClick={() => executeStageChange(true)}
                disabled={stageChangeSaving || !stageActivityTitle.trim()}
              >
                {stageChangeSaving ? <Loader2 size={14} className="animate-spin" /> : null}
                Guardar actividad y cambiar fase
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ── Lead Detail Panel ────────────────────────────────── */
function LeadDetailPanel({
  lead, stages, stageMap, onStageChange, onConvert, onDelete, onSave, saving, converting, onRecargar,
}: {
  lead: Lead;
  stages: PipelineStage[];
  stageMap: Map<string, PipelineStage>;
  onStageChange: (lead: Lead, stageId: string) => void;
  onConvert: (lead: Lead) => Promise<void>;
  onDelete: (lead: Lead) => void;
  onSave: (lead: Lead, updates: Record<string, any>) => Promise<void>;
  saving: boolean;
  converting: string | null;
  /** Vuelve a leer el lead del servidor (p. ej. tras apuntar una conversación que rellenó datos o movió de fase). */
  onRecargar?: (id: string) => void;
}) {
  const stage = stageMap.get(lead.pipelineStageId);
  const isLost = stage?.isLost;
  const navigate = useNavigate();
  const autor = useAutores();

  const [editing, setEditing] = useState(false);
  const [chatWhatsApp, setChatWhatsApp] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  // El formulario sale del lead al pulsar «Editar», no solo al abrir la ficha: si entretanto
  // algo lo cambió (p. ej. «Procesar conversación» rellenó la zona), guardar no lo borra
  const formDe = (l: Lead) => ({
    firstName: l.firstName || '',
    lastName: l.lastName || '',
    phone: l.phone || '',
    email: l.email || '',
    notes: l.notes || '',
    propertyAddress: l.propertyAddress || '',
    propertyZone: l.propertyZone || '',
    propertyType: l.propertyType || '',
    propertyBedrooms: l.propertyBedrooms?.toString() || '',
    propertyCurrentState: l.propertyCurrentState || '',
  });
  const [form, setForm] = useState(() => formDe(lead));

  const fetchActivities = useCallback(() => {
    setLoadingActivities(true);
    api.getActivities({ entityId: lead.id, entityType: 'lead' })
      .then(setActivities)
      .catch(() => setActivities([]))
      .finally(() => setLoadingActivities(false));
  }, [lead.id, lead.updatedAt]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const handleAddActivity = async (data: any) => {
    await api.createActivity({
      ...data,
      entityId: lead.id,
      entityType: 'lead',
    });
    setShowAddActivity(false);
    fetchActivities();
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (!confirm('¿Eliminar esta actividad?')) return;
    await api.deleteActivity(activityId);
    fetchActivities();
  };

  const handleSaveClick = () => {
    onSave(lead, {
      ...form,
      propertyBedrooms: form.propertyBedrooms ? Number(form.propertyBedrooms) : undefined,
    });
  };

  return (
    <div className="space-y-5">
      {chatWhatsApp && <WhatsAppLead lead={lead} onClose={() => setChatWhatsApp(false)} onProcesada={() => { fetchActivities(); onRecargar?.(lead.id); }} />}
      {/* Stage selector */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <span className="text-xs text-sereno-dark/40 font-medium">Fase:</span>
          <div className="flex gap-1.5 flex-wrap flex-1">
            {stages.sort((a, b) => a.order - b.order).map(s => (
              <button
                key={s.id}
                onClick={() => onStageChange(lead, s.id)}
                className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-all ${lead.pipelineStageId === s.id ? 'text-white shadow-sm' : 'bg-gray-100 text-sereno-dark/50 hover:bg-gray-200'}`}
                style={lead.pipelineStageId === s.id ? { backgroundColor: s.color } : {}}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
        {lead.pipelineStageChangedAt && (
          <p className="text-xs text-sereno-dark/40 flex items-center gap-1.5" title={lead.pipelineStageChangedAt}>
            <Clock size={12} className="flex-shrink-0" />
            <span>
              En {stage ? <>«{stage.name}»</> : 'esta fase'} desde el {formatDateTime(lead.pipelineStageChangedAt)}
              {' · '}{haceCuanto(lead.pipelineStageChangedAt)}
            </span>
          </p>
        )}
      </div>

      <div className="border-t border-gray-100" />

      {!editing ? (
        <>
          {/* Desktop: 2-column layout — Info left, Activity right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left column: Lead info */}
            <div className="lg:col-span-3 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Nombre</span>
                  <p className="text-sm font-medium">{lead.firstName} {lead.lastName}</p>
                </div>
                <div>
                  <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Fuente</span>
                  <p className="text-sm flex items-center gap-1">
                    {lead.source === 'facebook' && <Facebook size={12} className="text-blue-500" />}
                    {SOURCE_LABELS[lead.source] || lead.source}
                  </p>
                </div>
                <div>
                  <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Teléfono</span>
                  <p className="text-sm">{lead.phone || <span className="text-sereno-dark/25">—</span>}</p>
                  {lead.phone && (
                    <button
                      type="button"
                      onClick={() => setChatWhatsApp(true)}
                      className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    >
                      <MessageCircle size={13} /> WhatsApp
                    </button>
                  )}
                </div>
                <div>
                  <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Email</span>
                  <p className="text-sm truncate">{lead.email || <span className="text-sereno-dark/25">—</span>}</p>
                </div>
                <div>
                  <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Creado</span>
                  {lead.createdAt ? (
                    <>
                      <p className="text-sm" title={lead.createdAt}>{formatDateTime(lead.createdAt)}</p>
                      <p className="text-[11px] text-sereno-dark/35">
                        {haceCuanto(lead.createdAt)}{autor(lead.createdBy) && ` · ${autor(lead.createdBy)}`}
                      </p>
                    </>
                  ) : <p className="text-sm text-sereno-dark/25">—</p>}
                </div>
                <div>
                  <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Última modificación</span>
                  {lead.updatedAt && lead.updatedAt !== lead.createdAt ? (
                    <>
                      <p className="text-sm" title={lead.updatedAt}>{formatDateTime(lead.updatedAt)}</p>
                      <p className="text-[11px] text-sereno-dark/35">
                        {haceCuanto(lead.updatedAt)}{autor(lead.updatedBy) && ` · ${autor(lead.updatedBy)}`}
                      </p>
                    </>
                  ) : <p className="text-sm text-sereno-dark/25">Sin cambios desde el alta</p>}
                </div>
                {lead.convertedAt && (
                  <div className="col-span-2">
                    <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Convertido a propietario</span>
                    <p className="text-sm" title={lead.convertedAt}>
                      {formatDateTime(lead.convertedAt)}
                      <span className="text-[11px] text-sereno-dark/35"> · {haceCuanto(lead.convertedAt)}</span>
                      {lead.convertedToOwnerId && (
                        <button
                          onClick={() => navigate(`/owners/${lead.convertedToOwnerId}`)}
                          className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                        >
                          Ver propietario
                        </button>
                      )}
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs text-sereno-dark/40 font-medium flex items-center gap-1 mb-3"><Home size={12} /> Propiedad del lead</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Dirección</span>
                    <p className="text-sm">{lead.propertyAddress || <span className="text-sereno-dark/25">—</span>}</p>
                  </div>
                  <div>
                    <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Zona</span>
                    <p className="text-sm">{lead.propertyZone ? (ZONE_LABELS[lead.propertyZone] || lead.propertyZone) : <span className="text-sereno-dark/25">—</span>}</p>
                  </div>
                  <div>
                    <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Tipo</span>
                    <p className="text-sm">{lead.propertyType ? lead.propertyType.charAt(0).toUpperCase() + lead.propertyType.slice(1) : <span className="text-sereno-dark/25">—</span>}</p>
                  </div>
                  <div>
                    <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Habitaciones</span>
                    <p className="text-sm">{lead.propertyBedrooms ?? <span className="text-sereno-dark/25">—</span>}</p>
                  </div>
                  <div>
                    <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Estado actual</span>
                    <p className="text-sm">{lead.propertyCurrentState ? ({ empty: 'Vacía', owner_use: 'Uso propietario', long_term_rental: 'Alquiler largo', already_tourist_rental: 'Alquiler turístico' }[lead.propertyCurrentState] || lead.propertyCurrentState) : <span className="text-sereno-dark/25">—</span>}</p>
                  </div>
                </div>
              </div>

              {lead.notes && (
                <div className="border-t border-gray-100 pt-4">
                  <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider">Notas</span>
                  <p className="text-sm whitespace-pre-wrap mt-1 text-sereno-dark/70">{lead.notes}</p>
                </div>
              )}
            </div>

            {/* Right column: tareas y actividad. Las tareas van encima porque
                son lo que queda por hacer; la actividad es lo que ya pasó. */}
            <div className="lg:col-span-2 lg:border-l lg:border-gray-100 lg:pl-6 space-y-6">
              <BloqueTareas
                relatedType="lead"
                relatedId={lead.id}
                relatedName={`${lead.firstName || ''} ${lead.lastName || ''}`.trim() || null}
              />

              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] text-sereno-dark/35 uppercase tracking-wider font-semibold">Actividad</span>
                  <button onClick={() => setShowAddActivity(true)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    <Plus size={12} /> Añadir
                  </button>
                </div>
                <div className="max-h-[50vh] overflow-y-auto pr-1">
                  {loadingActivities ? (
                    <p className="text-sm text-sereno-dark/30">Cargando...</p>
                  ) : (
                    <ActivityTimeline
                      activities={activities}
                      onDelete={handleDeleteActivity}
                      nombreFase={id => stageMap.get(id)?.name}
                      valorLegible={valorDeCampo}
                      // Los de Facebook ya traen su «Lead captado» a la misma hora.
                      alta={lead.createdAt && !activities.some(a => a.activityType === 'lead_captured')
                        ? { titulo: 'Lead creado', fecha: lead.createdAt, por: lead.createdBy }
                        : undefined}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <button onClick={() => { setForm(formDe(lead)); setEditing(true); }} className="btn-ghost text-sm">Editar</button>
            <div className="flex items-center gap-2">
              <button onClick={() => onDelete(lead)} className="text-sm text-red-400 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors">
                Eliminar
              </button>
              {/* El contrato se genera y se firma siendo lead; la conversión a
                  propietario llega después y arrastra el contrato a su ficha. */}
              <button
                onClick={() => navigate(`/contracts/nuevo?tipo=lead&id=${lead.id}`)}
                className="btn-secondary text-sm flex items-center gap-1.5"
                title="Generar el contrato desde la plantilla, con los datos del lead"
              >
                <FileText size={14} />
                Generar contrato
              </button>
              {lead.convertedToOwnerId ? (
                <button
                  onClick={() => navigate(`/owners/${lead.convertedToOwnerId}`)}
                  className="btn-secondary text-sm flex items-center gap-1.5"
                >
                  <UserCheck size={14} />
                  Ver propietario
                </button>
              ) : !isLost && (
                <button
                  onClick={() => onConvert(lead)}
                  disabled={converting === lead.id}
                  className="btn-primary text-sm flex items-center gap-1.5"
                >
                  {converting === lead.id ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} />}
                  Convertir a propietario
                </button>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Nombre</label>
              <input className="input" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
            </div>
            <div>
              <label className="label">Apellidos</label>
              <input className="input" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
            </div>
            <div>
              <label className="label">Teléfono</label>
              <input className="input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
          </div>
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs text-sereno-dark/40 mb-3 flex items-center gap-1"><Home size={12} /> Propiedad</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="label">Dirección</label>
                <input className="input" value={form.propertyAddress} onChange={e => setForm(f => ({ ...f, propertyAddress: e.target.value }))} />
              </div>
              <div>
                <label className="label">Zona</label>
                <select className="input" value={form.propertyZone} onChange={e => setForm(f => ({ ...f, propertyZone: e.target.value }))}>
                  <option value="">Sin especificar</option>
                  {Object.entries(ZONE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Tipo</label>
                <select className="input" value={form.propertyType} onChange={e => setForm(f => ({ ...f, propertyType: e.target.value }))}>
                  <option value="">Sin especificar</option>
                  {['apartamento', 'casa', 'chalet', 'atico', 'estudio', 'villa', 'adosado'].map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Habitaciones</label>
                <input type="number" className="input" value={form.propertyBedrooms} onChange={e => setForm(f => ({ ...f, propertyBedrooms: e.target.value }))} />
              </div>
              <div>
                <label className="label">Estado actual</label>
                <select className="input" value={form.propertyCurrentState} onChange={e => setForm(f => ({ ...f, propertyCurrentState: e.target.value }))}>
                  <option value="">Sin especificar</option>
                  <option value="empty">Vacía</option>
                  <option value="owner_use">Uso del propietario</option>
                  <option value="long_term_rental">Alquiler largo plazo</option>
                  <option value="already_tourist_rental">Ya alquiler turístico</option>
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className="label">Notas</label>
            <textarea className="input" rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button className="btn-ghost" onClick={() => setEditing(false)}>Cancelar</button>
            <button className="btn-primary flex items-center gap-1.5" disabled={saving} onClick={handleSaveClick}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      )}

      <Modal isOpen={showAddActivity} onClose={() => setShowAddActivity(false)} title="Nueva actividad">
        <LeadActivityForm onSubmit={handleAddActivity} onCancel={() => setShowAddActivity(false)} />
      </Modal>
    </div>
  );
}

/** Los códigos que guarda la ficha, leídos como se ven en ella. */
function valorDeCampo(campo: string, valor: string): string {
  switch (campo) {
    case 'propertyZone': return ZONE_LABELS[valor] || valor;
    case 'propertyCurrentState': return PROPERTY_STATE_LABELS[valor] || valor;
    case 'source': return SOURCE_LABELS[valor] || valor;
    case 'preferredLanguage': return LANGUAGE_LABELS[valor] || valor;
    case 'propertyType': return valor.charAt(0).toUpperCase() + valor.slice(1);
    default: return valor;
  }
}

function LeadActivityForm({ onSubmit, onCancel }: { onSubmit: (data: any) => Promise<void>; onCancel: () => void }) {
  const [form, setForm] = useState({ activityType: 'note', title: '', description: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try { await onSubmit(form); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">Tipo</label>
        <select className="input" value={form.activityType} onChange={e => setForm(f => ({ ...f, activityType: e.target.value }))}>
          <option value="note">Nota</option>
          <option value="call">Llamada</option>
          <option value="email">Email</option>
          <option value="visit">Visita</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="meeting">Reunión</option>
        </select>
      </div>
      <div>
        <label className="label">Título *</label>
        <input className="input" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
      </div>
      <div>
        <label className="label">Descripción</label>
        <textarea className="input" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Guardando...' : 'Guardar'}</button>
      </div>
    </form>
  );
}
